import { useEffect, useMemo } from 'react'
import { DataTable } from '@/components/common/DataTable'
import { ColumnVisibilityControl } from '@/components/ui/ColumnVisibilityControl'
import { useColumnVisibility } from '@/hooks/useColumnVisibility'
import { LockedSalaryData } from '@/types/lockedSalaryData'
import {
  DEFAULT_EXPORT_COLUMNS,
  getVisibleExportColumns,
  createExportColumns
} from './exportConfig'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'

interface ExportDriverTableProps {
  data: LockedSalaryData[]
  sendingEmailTo: string | null
  downloadingPdfFor: string | null
  onSendEmail: (data: LockedSalaryData) => void
  onDownloadPdf: (data: LockedSalaryData) => void
  /** Callback to provide all columns for export dialog */
  onColumnsReady?: (columns: ReturnType<typeof createExportColumns>) => void
}

/**
 * Driver table with actions for the export page
 */
export function ExportDriverTable({
  data,
  sendingEmailTo,
  downloadingPdfFor,
  onSendEmail,
  onDownloadPdf,
  onColumnsReady
}: ExportDriverTableProps) {
  const {
    columns: columnConfig,
    visibleColumns: visibleColumnConfig,
    updateColumnVisibility,
    resetToDefaults
  } = useColumnVisibility({
    storageKey: 'export-table-columns',
    defaultColumns: DEFAULT_EXPORT_COLUMNS
  })

  // Get the actual columns to render
  const visibleColumnKeys = useMemo(() => visibleColumnConfig.map((col) => col.key), [visibleColumnConfig])
  const tableColumns = useMemo(() => {
    return getVisibleExportColumns(
      visibleColumnKeys,
      sendingEmailTo,
      downloadingPdfFor,
      onSendEmail,
      onDownloadPdf
    )
  }, [visibleColumnKeys, sendingEmailTo, downloadingPdfFor, onSendEmail, onDownloadPdf])

  // Create all columns for export (without actions column)
  const allExportColumns = useMemo(() => {
    const cols = createExportColumns(sendingEmailTo, downloadingPdfFor, onSendEmail, onDownloadPdf)
    // Filter out the actions column for export
    return cols.filter((col) => col.header !== 'Åtgärder' && col.header !== 'Status')
  }, [sendingEmailTo, downloadingPdfFor, onSendEmail, onDownloadPdf])

  // Notify parent about columns
  useEffect(() => {
    onColumnsReady?.(allExportColumns)
  }, [allExportColumns, onColumnsReady])

  if (data.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <p className="text-center text-muted-foreground">Inga förare hittades för denna period.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div>
          <CardTitle className="text-lg">Förare</CardTitle>
          <CardDescription>{data.length} förare i perioden</CardDescription>
        </div>
        <ColumnVisibilityControl
          columns={columnConfig}
          onVisibilityChange={updateColumnVisibility}
          onResetToDefaults={resetToDefaults}
        />
      </CardHeader>
      <CardContent className="p-0">
        <DataTable
          data={data}
          columns={tableColumns}
          getRowId={(row) => row.id}
          emptyMessage="Inga förare hittades"
        />
      </CardContent>
    </Card>
  )
}
